import { MagnifyingGlassIcon, PlusIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Input } from '../../../components/input/Input';
import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table } from '../../../components/table/Table';
import { fetchCustomerPortalQuotationRequests } from './models';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { useTranslation } from '../../../contexts/translation-context';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';

export const CustomerPortalQuotationRequestsPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const { customer } = useCustomerPortalCustomer();
  const { i18n } = useTranslation();
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return fetchCustomerPortalQuotationRequests({
        customerId: customer.id,
        cursor: variables.cursor,
        take: variables.take,
        filters: {
          search: searchValue,
        },
      });
    },
    [customer.id, searchValue],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  useEffect(() => {
    page.reset();
  }, [searchValue]);

  const tableHeaders: ITableHeader[] = [
    {
      id: 'reference',
      name: i18n('customerPortal.quotes.quoteReference'),
    },
    {
      id: 'quote',
      name: i18n('customerPortal.quotes.quote'),
    },
    {
      id: 'date',
      name: i18n('customerPortal.quotes.requestedAt'),
    },
  ];

  const title = i18n('customerPortal.quotes.requests');
  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: i18n('customerPortal.quotes.quotes'),
                  to: '..',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <LinkButton color="primary" iconLeft={<PlusIcon className="button-icon" />} to="new">
                {i18n('customerPortal.quotes.request')}
              </LinkButton>
            }
          />
        }
        filters={
          <Input
            type="text"
            placeholder={i18n('customerPortal.quotes.search')}
            value={searchValue}
            onChange={setSearchValue}
            iconLeft={<MagnifyingGlassIcon className="input-icon" />}
          />
        }
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={tableHeaders}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          mapData={(request) => {
            return [
              <Link to={`${request.id}`} className="link-text">
                {request.reference ?? i18n('draft')}
              </Link>,
              request.quotationId ? <Link to={`../${request.quotationId}`}>{request.reference}</Link> : 'n/a',
              formatDate(request.createdAt),
            ];
          }}
        />
      </ListPage>
    </>
  );
};
